import React, { useState, useEffect, useCallback } from 'react';
import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridColumnVisibilityModel,
  GridSortModel,
} from '@mui/x-data-grid';
import axios from 'axios';
import Container from 'react-bootstrap/Container';
import Col from 'react-bootstrap/esm/Col';
import Row from 'react-bootstrap/Row';
import './cardsDashboard.css';

type SoldStatus = 'all' | 'sold' | 'unsold';

interface FilterPreset {
  name: string;
  searchQuery: string;
  filters: Record<string, string>;
  minPrice: string;
  maxPrice: string;
  soldStatus: SoldStatus;
}

const PRESET_STORAGE_KEY = 'at_cardsdb_filter_presets';
const COLUMN_STORAGE_KEY = 'at_cardsdb_column_visibility';
const SORT_STORAGE_KEY = 'at_cardsdb_sort_model';

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

function Cards() {
  const [data, setData] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Dropdown filters (same fields as SearchPage)
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [filterOptions, setFilterOptions] = useState<Record<string, string[]>>({
    card_game: [],
    card_set_year: [],
    card_set: [],
    card_language: [],
    card_grader: [],
    card_grade_number: [],
    card_owner: [],
    tag_id: [],
  });

  // Extra filters
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [soldStatus, setSoldStatus] = useState<SoldStatus>('all');

  // Presets
  const [presets, setPresets] = useState<FilterPreset[]>([]);
  const [selectedPresetName, setSelectedPresetName] = useState('');
  const [newPresetName, setNewPresetName] = useState('');
  // Collapse filters
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  // Pro-feel extras
  const [loading, setLoading] = useState(false);

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(() => {
      try {
        const stored = localStorage.getItem(COLUMN_STORAGE_KEY);
        return stored ? JSON.parse(stored) : {};
      } catch {
        return {};
      }
    });

  const [sortModel, setSortModel] = useState<GridSortModel>(() => {
    try {
      const stored = localStorage.getItem(SORT_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // DataGrid columns
  const columns: GridColDef[] = [
    { field: 'card_id', headerName: 'ID', width: 50, align: 'center' },
    { field: 'card_game', headerName: 'Game', width: 80, headerAlign: 'center', align: 'center' },
    {
      field: 'cover_image',
      headerName: 'Card',
      width: 90,
      align: 'center',
      sortable: false,
      renderCell: (params) => {
        let parsed = JSON.parse(params.value as string);
        let imageUrl;

        if (parsed[0]?.thumbnail === 'none') {
          imageUrl = 'http://alientrader.net/cards/cards/images/cards/Temp.jpg';
        } else {
          if (parsed[0]?.thumbnail.includes('/home/u825454141/domains/alientrader.net/public_html/')) {
            imageUrl = parsed[0]?.thumbnail.replace(
              '/home/u825454141/domains/alientrader.net/public_html/',
              'http://alientrader.net/'
            );
          } else {
            imageUrl = 'https://api.alientrader.cards/' + parsed[0]?.thumbnail;
          }
        }
        let cardId = params.row.card_id;
        return (
          <div>
            <a href={`/card/${cardId}`}>
              <img src={imageUrl} alt="card" style={{ height: '100px', borderRadius: '5px' }} />
            </a>
          </div>
        );
      },
    },
    {
      field: 'card_pokemon',
      headerName: 'Pokemon',
      width: 140,
      renderCell: (params) => {
        let cardId = params.row.card_id;
        return <a href={`/card/${cardId}`}>{params.value}</a>;
      },
    },
    { field: 'card', headerName: 'Variety / Pedigree', width: 170 },
    { field: 'card_set_year', headerName: 'Year', width: 70 },
    { field: 'card_set', headerName: 'Set', width: 180 },
    { field: 'card_language', headerName: 'Language', width: 90 },
    { field: 'card_grader', headerName: 'Grader', width: 80, align: 'center' },
    { field: 'card_grade_number', headerName: 'Grade #', width: 80, align: 'center' },
    { field: 'card_owner', headerName: 'Owner', width: 90 },
    { field: 'tag_names', headerName: 'Tags', width: 180 },
    {
      field: 'card_total_cost',
      headerName: 'Total Cost',
      width: 100,
      align: 'right',
      valueFormatter: (params) => {
        const v = Number(params.value ?? 0);
        return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
      },
    },
    {
      field: 'card_sold_price',
      headerName: 'Sold Price',
      width: 100,
      align: 'right',
      valueFormatter: (params) => {
        const v = Number(params.value ?? 0);
        if (!v) return '-';
        return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
      },
    },
    {
      field: 'card_profit',
      headerName: 'Profit',
      width: 90,
      align: 'right',
      valueFormatter: (params) => {
        const v = Number(params.value ?? 0);
        if (!v) return '-';
        return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
      },
    },
    {
      field: 'card_date_received',
      headerName: 'Received',
      width: 100,
      valueFormatter: (params) => {
        if (!params.value) return '';
        const d = new Date(params.value as string);
        return d.toLocaleDateString();
      },
    },
    {
      field: 'card_sold_date',
      headerName: 'Sold Date',
      width: 100,
      valueFormatter: (params) => {
        if (!params.value) return '-';
        const d = new Date(params.value as string);
        return d.toLocaleDateString();
      },
    },
  ];

  // Toolbar
  function CardsToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
      </GridToolbarContainer>
    );
  }

  // Fetch filter options
  const fetchFilterOptions = async (
    field: string,
    sortType: 'alphabetical' | 'numerical' = 'alphabetical',
    currentFilters: Record<string, string> = {}
  ) => {
    try {
      const response = await axios.get('https://api.alientrader.cards/data/filter-options', {
        params: { field, ...currentFilters },
      });

      let sortedOptions: string[] = response.data;

      if (sortType === 'alphabetical') {
        sortedOptions = sortedOptions.sort((a, b) => a.localeCompare(b));
      } else if (sortType === 'numerical') {
        sortedOptions = sortedOptions.sort((a, b) => Number(b) - Number(a));
      }

      sortedOptions.unshift('All');

      setFilterOptions((prev) => ({
        ...prev,
        [field]: sortedOptions,
      }));
    } catch (error) {
      console.error(`Error fetching filter options for ${field}:`, error);
    }
  };

  // Fetch DataGrid results
  const fetchResults = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://api.alientrader.cards/data/dbsearch', {
        params: {
          q: debouncedSearchQuery,
          ...filters,
          minPrice: minPrice || undefined,
          maxPrice: maxPrice || undefined,
          soldStatus: soldStatus === 'all' ? undefined : soldStatus,
        },
      });

      setData(response.data);
    } catch (error) {
      console.error('Error fetching results:', error);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchQuery, filters, minPrice, maxPrice, soldStatus]);

  // Initial load: presets, dropdown options, and initial results
  useEffect(() => {
    // presets
    try {
      const stored = localStorage.getItem(PRESET_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as FilterPreset[];
        setPresets(parsed);
      }
    } catch (e) {
      console.error('Error loading presets from localStorage', e);
    }

    // dropdowns (no filters yet)
    fetchFilterOptions('card_game', 'alphabetical', {});
    fetchFilterOptions('card_set', 'alphabetical', {});
    fetchFilterOptions('card_set_year', 'numerical', {});
    fetchFilterOptions('card_language', 'alphabetical', {});
    fetchFilterOptions('card_grader', 'alphabetical', {});
    fetchFilterOptions('card_grade_number', 'numerical', {});
    fetchFilterOptions('card_owner', 'alphabetical', {});
    axios.get('https://api.alientrader.cards/tags')
      .then((response) => {
        const tagOptions = ['All', ...(response.data || []).map((tag: any) => `${tag.tag_id}|${tag.tag_name}`)];
        setFilterOptions((prev) => ({ ...prev, tag_id: tagOptions }));
      })
      .catch((error) => {
        console.error('Error fetching tags:', error);
      });

    // initial results
    fetchResults();
  }, [fetchResults]);

  // Re-fetch results when query / filters / price / soldStatus change
  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  // When a filter is changed
  const handleFilterChange = (field: string, value: string) => {
    const updatedFilters: Record<string, string> = {
      ...filters,
      [field]: value === 'All' ? '' : value,
    };
    setFilters(updatedFilters);

    // update other dropdowns based on new filters
    const otherFields = Object.keys(filterOptions).filter((f) => f !== field && f !== 'tag_id');
    otherFields.forEach((f) => {
      const sortType = f === 'card_set_year' || f === 'card_grade_number' ? 'numerical' : 'alphabetical';
      fetchFilterOptions(f, sortType, updatedFilters);
    });
  };

  const handleReset = () => {
    setSearchQuery('');
    setFilters({});
    setMinPrice('');
    setMaxPrice('');
    setSoldStatus('all');

    // reload dropdowns w/ no filters
    fetchFilterOptions('card_game', 'alphabetical', {});
    fetchFilterOptions('card_set', 'alphabetical', {});
    fetchFilterOptions('card_set_year', 'numerical', {});
    fetchFilterOptions('card_language', 'alphabetical', {});
    fetchFilterOptions('card_grader', 'alphabetical', {});
    fetchFilterOptions('card_grade_number', 'numerical', {});
    fetchFilterOptions('card_owner', 'alphabetical', {});
  };

  // Presets
  const savePresetsToStorage = (list: FilterPreset[]) => {
    setPresets(list);
    try {
      localStorage.setItem(PRESET_STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      console.error('Error saving presets to localStorage', e);
    }
  };

  const handleSavePreset = () => {
    const trimmedName = newPresetName.trim();
    if (!trimmedName) return;

    const newPreset: FilterPreset = {
      name: trimmedName,
      searchQuery,
      filters,
      minPrice,
      maxPrice,
      soldStatus,
    };

    const existingIndex = presets.findIndex((p) => p.name === trimmedName);
    let updated: FilterPreset[];

    if (existingIndex >= 0) {
      updated = [...presets];
      updated[existingIndex] = newPreset;
    } else {
      updated = [...presets, newPreset];
    }

    savePresetsToStorage(updated);
    setSelectedPresetName(trimmedName);
    setNewPresetName('');
  };

  const handleApplyPreset = () => {
    const preset = presets.find((p) => p.name === selectedPresetName);
    if (!preset) return;

    setSearchQuery(preset.searchQuery);
    setFilters(preset.filters || {});
    setMinPrice(preset.minPrice);
    setMaxPrice(preset.maxPrice);
    setSoldStatus(preset.soldStatus);
  };

  const handleDeletePreset = () => {
    if (!selectedPresetName) return;
    const updated = presets.filter((p) => p.name !== selectedPresetName);
    savePresetsToStorage(updated);
    setSelectedPresetName('');
  };

  // Summary metrics
  const totalCards = data.length;

  const unsoldCards = data.filter((row) => {
    // treat null / undefined / 0 sold price as unsold
    return !row.card_sold_price && !row.card_sold_date;
  }).length;

  const totalCost = data.reduce((sum, row) => {
    const cost = Number(row.card_total_cost ?? row.card_price ?? 0);
    return sum + (isNaN(cost) ? 0 : cost);
  }, 0);

  const totalProfit = data.reduce((sum, row) => {
    const profit = Number(row.card_profit ?? 0);
    return sum + (isNaN(profit) ? 0 : profit);
  }, 0);

  const formattedTotalCost = totalCost.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  const formattedTotalProfit = totalProfit.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  const handleRowClick = (params: any) => {
    window.location.href = `/card/${params.row.card_id}`;
  };

  // Persist column + sort changes
  const handleColumnVisibilityChange = (model: GridColumnVisibilityModel) => {
    setColumnVisibilityModel(model);
    try {
      localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(model));
    } catch (e) {
      console.error('Error saving column visibility', e);
    }
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
    try {
      localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(model));
    } catch (e) {
      console.error('Error saving sort model', e);
    }
  };

  return (
    <div className="cardsDashboard">
      <Container fluid>
        {/* Page header */}
        <Row className="cards-page-header">
          <Col>
            <h2 className="cards-page-title">Inventory Search</h2>
            <span className="cards-page-subtitle">
              Search, filter and analyze all graded cards in your inventory.
            </span>
          </Col>
        </Row>

        {/* Filters card */}
        <div className="cards-filters shadow-sm">
          {/* Search + toggle row */}
          <Row className="cards-filter-row align-items-center">
            <Col md={6} sm={12} className="mb-2 mb-md-0">
              <input
                className="search cards-search-input"
                placeholder="Search name, set, etc..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Col>

            <Col md={6} sm={12} className="text-md-end">
              <button
                className="cards-toggle-btn"
                onClick={() => setFiltersCollapsed((prev) => !prev)}
              >
                {filtersCollapsed ? 'Show filters' : 'Hide filters'}
              </button>
            </Col>
          </Row>

          {/* Everything below hides/shows when collapsed */}
          {!filtersCollapsed && (
            <>
              {/* Main dropdown filters */}
              <Row className="cards-filter-row mt-2">
                <Col md={12} sm={12}>
                  <div className="d-flex flex-wrap gap-2">
                    <div className="cards-filter-group">
                      <span className="cards-filter-label">Game</span>
                      <select
                        id="card_game"
                        value={filters['card_game'] || 'All'}
                        onChange={(e) => handleFilterChange('card_game', e.target.value)}
                        className="cards-filter-control"
                      >
                        {filterOptions['card_game']?.map((option, idx) => (
                          <option key={idx} value={option === 'All' ? '' : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="cards-filter-group">
                      <span className="cards-filter-label">Set</span>
                      <select
                        id="card_set"
                        value={filters['card_set'] || 'All'}
                        onChange={(e) => handleFilterChange('card_set', e.target.value)}
                        className="cards-filter-control"
                      >
                        {filterOptions['card_set']?.map((option, idx) => (
                          <option key={idx} value={option === 'All' ? '' : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="cards-filter-group">
                      <span className="cards-filter-label">Year</span>
                      <select
                        id="card_set_year"
                        value={filters['card_set_year'] || 'All'}
                        onChange={(e) => handleFilterChange('card_set_year', e.target.value)}
                        className="cards-filter-control cards-filter-control--small"
                      >
                        {filterOptions['card_set_year']?.map((option, idx) => (
                          <option key={idx} value={option === 'All' ? '' : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="cards-filter-group">
                      <span className="cards-filter-label">Language</span>
                      <select
                        id="card_language"
                        value={filters['card_language'] || 'All'}
                        onChange={(e) => handleFilterChange('card_language', e.target.value)}
                        className="cards-filter-control cards-filter-control--small"
                      >
                        {filterOptions['card_language']?.map((option, idx) => (
                          <option key={idx} value={option === 'All' ? '' : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="cards-filter-group">
                      <span className="cards-filter-label">Grader</span>
                      <select
                        id="card_grader"
                        value={filters['card_grader'] || 'All'}
                        onChange={(e) => handleFilterChange('card_grader', e.target.value)}
                        className="cards-filter-control cards-filter-control--small"
                      >
                        {filterOptions['card_grader']?.map((option, idx) => (
                          <option key={idx} value={option === 'All' ? '' : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="cards-filter-group">
                      <span className="cards-filter-label">Grade</span>
                      <select
                        id="card_grade_number"
                        value={filters['card_grade_number'] || 'All'}
                        onChange={(e) => handleFilterChange('card_grade_number', e.target.value)}
                        className="cards-filter-control cards-filter-control--tiny"
                      >
                        {filterOptions['card_grade_number']?.map((option, idx) => (
                          <option key={idx} value={option === 'All' ? '' : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="cards-filter-group">
                      <span className="cards-filter-label">Owner</span>
                      <select
                        id="card_owner"
                        value={filters['card_owner'] || 'All'}
                        onChange={(e) => handleFilterChange('card_owner', e.target.value)}
                        className="cards-filter-control cards-filter-control--small"
                      >
                        {filterOptions['card_owner']?.map((option, idx) => (
                          <option key={idx} value={option === 'All' ? '' : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="cards-filter-group">
                      <span className="cards-filter-label">Tag</span>
                      <select
                        id="tag_id"
                        value={filters['tag_id'] || 'All'}
                        onChange={(e) => handleFilterChange('tag_id', e.target.value)}
                        className="cards-filter-control cards-filter-control--small"
                      >
                        {filterOptions['tag_id']?.map((option, idx) => {
                          const [value, label] = option.includes('|') ? option.split('|') : [option, option];
                          return (
                            <option key={idx} value={value === 'All' ? '' : value}>
                              {label}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Cost / sold filters + reset */}
              <Row className="cards-filter-row mt-2 align-items-center">
                <Col md={6} sm={12}>
                  <div className="d-flex flex-wrap gap-2">
                    <div className="cards-filter-group">
                      <span className="cards-filter-label">Min Cost</span>
                      <input
                        type="number"
                        placeholder="Min"
                        value={minPrice}
                        onChange={(e) => setMinPrice(e.target.value)}
                        className="cards-filter-control cards-filter-control--small"
                      />
                    </div>
                    <div className="cards-filter-group">
                      <span className="cards-filter-label">Max Cost</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(e.target.value)}
                        className="cards-filter-control cards-filter-control--small"
                      />
                    </div>
                    <div className="cards-filter-group">
                      <span className="cards-filter-label">Sold</span>
                      <select
                        value={soldStatus}
                        onChange={(e) => setSoldStatus(e.target.value as SoldStatus)}
                        className="cards-filter-control cards-filter-control--small"
                      >
                        <option value="all">All</option>
                        <option value="unsold">Unsold</option>
                        <option value="sold">Sold</option>
                      </select>
                    </div>
                  </div>
                </Col>

                <Col md={6} sm={12} className="text-md-end mt-2 mt-md-0">
                  <button className="cards-reset-btn" onClick={handleReset}>
                    Reset Filters
                  </button>
                </Col>
              </Row>

              {/* Presets row */}
              <Row className="cards-presets-row mt-2 align-items-center">
                <Col md={4} sm={12} className="mb-2 mb-md-0">
                  <select
                    value={selectedPresetName}
                    onChange={(e) => setSelectedPresetName(e.target.value)}
                    className="cards-filter-control cards-filter-control--full"
                  >
                    <option value="">Select preset...</option>
                    {presets.map((p) => (
                      <option key={p.name} value={p.name}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </Col>

                <Col md={3} sm={12} className="d-flex gap-2 mb-2 mb-md-0">
                  <button
                    className="cards-preset-btn"
                    onClick={handleApplyPreset}
                    disabled={!selectedPresetName}
                  >
                    Apply
                  </button>
                  <button
                    className="cards-preset-btn"
                    onClick={handleDeletePreset}
                    disabled={!selectedPresetName}
                  >
                    Delete
                  </button>
                </Col>

                <Col md={5} sm={12} className="d-flex gap-2">
                  <input
                    type="text"
                    placeholder="Preset name"
                    value={newPresetName}
                    onChange={(e) => setNewPresetName(e.target.value)}
                    className="cards-filter-control cards-filter-control--full"
                  />
                  <button className="cards-preset-btn" onClick={handleSavePreset}>
                    Save Preset
                  </button>
                </Col>
              </Row>
            </>
          )}
        </div>
        {/* Summary line */}
        <Row className="cards-summary-row">
          <Col>
            <div className="cards-summary">
              <span>
                Showing <strong>{totalCards.toLocaleString()}</strong> cards
              </span>
              <span>
                • <strong>{unsoldCards.toLocaleString()}</strong> unsold
              </span>
              <span>
                • Total cost <strong>{formattedTotalCost}</strong>
              </span>
              <span>
                • Total profit <strong>{formattedTotalProfit}</strong>
              </span>
            </div>
          </Col>
        </Row>

        {/* DataGrid */}
        <Row style={{ marginTop: 8 }}>
          <div style={{ height: 680, width: '100%' }}>
            <DataGrid
              rows={data}
              columns={columns}
              getRowId={(row) => row.card_id}
              rowHeight={110}
              density="compact"
              disableRowSelectionOnClick
              loading={loading}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={handleColumnVisibilityChange}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              slots={{ toolbar: CardsToolbar }}
              localeText={{
                noRowsLabel: 'No cards match your filters.',
              }}
              sx={{
                fontSize: 12,
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f5f5f5',
                },
                '& .MuiDataGrid-row:nth-of-type(odd)': {
                  backgroundColor: '#fafafa',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#eef7ff',
                },
              }}
              onRowClick={handleRowClick}
            />
          </div>
        </Row>
      </Container>
    </div>
  );
}

export default Cards;
